import { useState } from 'react';
import { agreementsApi } from '@/modules/agreements/services/api';
import { useSigningSession } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing-session';
import { useSignatureIdentity } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signature-identity';
import type {
  AgreementExecutionResponse,
  SignAgreementRequest,
} from '@/api/schemas/agreements.types';

/**
 * The two methods a signer can perform interactively.
 *
 * `checkbox` is a third wire value, but it is not a signature a person draws or uploads —
 * it is a recorded tick. This surface never offers it.
 */
export type InteractiveSigningMethod = 'drawn' | 'uploaded';

export type CapturedSignature = {
  objectKey: string;
  mimeType: 'image/png' | 'image/jpeg';
  byteSize: number;
};

/** Build the final `sign` payload from the session, the captured image and the signer's identity. */
function buildSignRequest(input: {
  challenge: string;
  contentHash: string;
  signingMethod: InteractiveSigningMethod;
  signature: CapturedSignature;
  signerName: string;
  signerTitle: string;
}): SignAgreementRequest {
  return {
    challenge: input.challenge,
    contentHash: input.contentHash,
    signingMethod: input.signingMethod,
    signerName: input.signerName.trim(),
    signerTitle: input.signerTitle.trim() || null,
    signerCapacity: null,
    signatureObjectKey: input.signature.objectKey,
    signatureMimeType: input.signature.mimeType,
    signatureByteSize: input.signature.byteSize,
  };
}

/**
 * The signing flow's state and its final commit.
 *
 * The flow is deliberately staged and each stage is a separate server call:
 *   1. open a signing session — the SERVER issues the challenge and binds the content hash;
 *   2. review the exact content the subject is bound by;
 *   3. capture a signature image (drawn or uploaded);
 *   4. complete a fresh identity check when the requirement demands one;
 *   5. sign, which records the execution.
 *
 * The challenge is NEVER invented by the client. Every stage that names a session — the
 * signature upload, the step-up ceremony and the final sign — carries the challenge the
 * server issued when the session was opened. A client-generated value would not resolve to
 * any session and the server would refuse it.
 *
 * No stage completes the execution on its own. In particular, uploading an image or ticking a
 * box does not sign — only the final `sign` call does, and it carries the content hash the
 * subject reviewed.
 */
export function useSigning(
  executionId: string,
  execution: AgreementExecutionResponse | null | undefined,
) {
  const [signingMethod, setSigningMethod] = useState<InteractiveSigningMethod>('drawn');
  const { session, error: sessionError, ensureSession } = useSigningSession(executionId);
  const identity = useSignatureIdentity();
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const needsFreshCheck = execution?.requiredAction === 'signature';
  const canSign =
    Boolean(execution) &&
    Boolean(identity.signature) &&
    identity.signerName.trim().length > 0 &&
    (!needsFreshCheck || Boolean(verificationId)) &&
    !busy;

  async function sign(onSigned: () => void): Promise<void> {
    if (!execution || !identity.signature) return;
    setBusy(true);
    setError(null);
    try {
      const opened = await ensureSession(signingMethod);
      await agreementsApi.sign(
        executionId,
        buildSignRequest({
          challenge: opened.challenge,
          contentHash: opened.contentHash,
          signingMethod,
          signature: identity.signature,
          signerName: identity.signerName,
          signerTitle: identity.signerTitle,
        }),
      );
      onSigned();
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setBusy(false);
    }
  }

  return {
    signingMethod,
    setSigningMethod,
    session,
    sessionError,
    ensureSession,
    ...identity,
    verificationId,
    setVerificationId,
    busy,
    error,
    needsFreshCheck,
    canSign,
    sign,
  };
}
